import { useEffect, useMemo, useState } from "react";
import useSWR from "swr";

import { healthOf, outstandingChecklist, type SectionKey } from "../health";
import { Field, Section, ToggleRow, errText, relativeTime, useToast } from "../ui";
import {
  api,
  type AdminClientDetail,
  type AdminClientForm,
  type AdminUsersPage,
} from "../../services/api";

/**
 * Overview: the four numbers that say how this customer is doing, the
 * outstanding-setup checklist, and the two things that belong to the customer
 * record itself (identity + whether the portal is on at all).
 *
 * Company details and Portal status are separate save scopes: switching the
 * portal off is a different kind of decision from fixing a phone number, and
 * the save bar should say which one you are committing.
 */
export function OverviewSection({
  client,
  onSaved,
  onGoTo,
}: {
  client: AdminClientDetail;
  onSaved: () => Promise<void>;
  onGoTo: (key: SectionKey) => void;
}) {
  const health = healthOf(client);

  const { data: forms } = useSWR<AdminClientForm[]>(
    ["/api/admin/clients", client.id, "forms"],
    () => api.admin.clients.listForms(client.id),
  );
  const { data: users } = useSWR<AdminUsersPage>(
    ["/api/admin/users", client.id, "overview"],
    () =>
      api.admin.users.list({ company_id: client.id, page: 1, page_size: 200 }),
  );

  const activeForms = (forms ?? []).filter((f) => f.is_published && f.enabled).length;
  const publishedForms = (forms ?? []).filter((f) => f.is_published).length;
  const items = users?.items ?? [];
  const activeUsers = items.filter((u) => u.status === "active");
  const neverSignedIn = activeUsers.filter((u) => !u.last_login_at).length;

  const outstanding = client.setup_health ? outstandingChecklist(health) : [];

  return (
    <>
      <div className="ad-stats">
        <div className="ad-stat">
          <div className="l">Portal users</div>
          <div className="v">{activeUsers.length}</div>
          <div className="s">{neverSignedIn} never signed in</div>
        </div>
        <div className="ad-stat">
          <div className="l">Active forms</div>
          <div className="v">{activeForms}</div>
          <div className="s">of {publishedForms} published</div>
        </div>
        <div className="ad-stat">
          <div className="l">Tickets · 30d</div>
          <div className="v">
            {client.tickets_30d == null && client.health_error
              ? "Unavailable"
              : (client.tickets_30d ?? 0)}
          </div>
          <div className="s">
            {client.tickets_30d == null && client.health_error
              ? "could not be read"
              : "from the Autotask mirror"}
          </div>
        </div>
        <div className="ad-stat">
          <div className="l">Setup</div>
          <div className="v">
            {health.done}
            <span style={{ fontSize: 14, color: "var(--iq-text-faint)" }}>
              {" "}
              / {health.total}
            </span>
          </div>
          <div className="s">
            {!client.setup_health
              ? "could not be read"
              : (health.unavailable ?? []).length > 0
                ? "some checks unavailable"
                : health.done === health.total
                  ? "complete"
                  : `${health.total - health.done} to go`}
          </div>
        </div>
      </div>

      {outstanding.length > 0 && (
        <Section
          title="Finish setting up"
          desc="Each item links straight to the section that fixes it."
        >
          {outstanding.map((item) => (
            <button
              key={item.key}
              type="button"
              className={`ad-check ${health[item.key]}`}
              style={{
                width: "100%",
                background: "none",
                borderTop: 0,
                borderLeft: 0,
                borderRight: 0,
                cursor: "pointer",
                textAlign: "left",
                font: "inherit",
              }}
              onClick={() => onGoTo(item.key)}
            >
              <span className="mark" />
              <span>
                <span className="t">{item.title}</span>
                <br />
                <span className="s">{item.blurb}</span>
              </span>
              <span style={{ flex: 1 }} />
              <span className="go">
                {health[item.key] === "warn" ? "Review" : "Set up"} →
              </span>
            </button>
          ))}
        </Section>
      )}

      <CompanyDetails client={client} onSaved={onSaved} />
      <PortalStatus client={client} onSaved={onSaved} />
    </>
  );
}

function CompanyDetails({
  client,
  onSaved,
}: {
  client: AdminClientDetail;
  onSaved: () => Promise<void>;
}) {
  const toast = useToast();
  const [draft, setDraft] = useState(() => fromClient(client));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(client.updated_at);

  useEffect(() => {
    setDraft(fromClient(client));
  }, [client]);

  const dirty = useMemo(
    () => JSON.stringify(draft) !== JSON.stringify(fromClient(client)),
    [draft, client],
  );

  async function save() {
    if (!draft.name.trim()) {
      setError("Portal name is required.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await api.admin.clients.update(client.id, {
        name: draft.name.trim(),
        phone: draft.phone.trim() || null,
        address1: draft.address1.trim() || null,
        address2: draft.address2.trim() || null,
        city: draft.city.trim() || null,
        state: draft.state.trim() || null,
        postal_code: draft.postalCode.trim() || null,
        web_address: draft.webAddress.trim() || null,
      });
      await onSaved();
      setSavedAt(new Date().toISOString());
      toast("Saved Company details");
    } catch (e) {
      setError(errText(e, "Save failed."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Section
      title="Company details"
      short="details"
      desc="Seeded from Autotask when the customer was created. Edits here don't write back."
      dirty={dirty}
      saving={saving}
      error={error}
      savedLabel={relativeTime(savedAt)}
      onSave={save}
      onDiscard={() => {
        setDraft(fromClient(client));
        setError(null);
      }}
    >
      <div className="ad-grid">
        <Field label="Portal name">
          <input
            value={draft.name}
            maxLength={200}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
        </Field>
        <Field label="Autotask company" note="Linked at onboarding; changing it re-points every ticket.">
          <input
            readOnly
            value={
              client.autotask_company_id != null
                ? `AT ${client.autotask_company_id}`
                : "Not linked"
            }
          />
        </Field>
        <Field label="Phone">
          <input
            value={draft.phone}
            maxLength={40}
            onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
          />
        </Field>
        <Field label="Website">
          <input
            value={draft.webAddress}
            maxLength={255}
            onChange={(e) => setDraft({ ...draft, webAddress: e.target.value })}
          />
        </Field>
        <Field label="Address line 1" span>
          <input
            value={draft.address1}
            maxLength={255}
            onChange={(e) => setDraft({ ...draft, address1: e.target.value })}
          />
        </Field>
        <Field label="Address line 2" span>
          <input
            value={draft.address2}
            maxLength={255}
            onChange={(e) => setDraft({ ...draft, address2: e.target.value })}
          />
        </Field>
        <Field label="City">
          <input
            value={draft.city}
            maxLength={100}
            onChange={(e) => setDraft({ ...draft, city: e.target.value })}
          />
        </Field>
        <Field label="State / postcode">
          <div style={{ display: "flex", gap: 8 }}>
            <input
              value={draft.state}
              maxLength={50}
              aria-label="State"
              onChange={(e) => setDraft({ ...draft, state: e.target.value })}
            />
            <input
              value={draft.postalCode}
              maxLength={20}
              aria-label="Postcode"
              onChange={(e) => setDraft({ ...draft, postalCode: e.target.value })}
            />
          </div>
        </Field>
      </div>
    </Section>
  );
}

function PortalStatus({
  client,
  onSaved,
}: {
  client: AdminClientDetail;
  onSaved: () => Promise<void>;
}) {
  const toast = useToast();
  const [isActive, setIsActive] = useState(client.is_active);
  const [notifications, setNotifications] = useState(client.notifications_enabled);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(client.updated_at);

  useEffect(() => {
    setIsActive(client.is_active);
    setNotifications(client.notifications_enabled);
  }, [client]);

  const dirty =
    isActive !== client.is_active || notifications !== client.notifications_enabled;

  async function save() {
    setSaving(true);
    setError(null);
    try {
      await api.admin.clients.update(client.id, {
        is_active: isActive,
        notifications_enabled: notifications,
      });
      await onSaved();
      setSavedAt(new Date().toISOString());
      toast("Saved Portal status");
    } catch (e) {
      setError(errText(e, "Save failed."));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Section
      title="Portal status"
      short="status"
      dirty={dirty}
      saving={saving}
      error={error}
      savedLabel={relativeTime(savedAt)}
      onSave={save}
      onDiscard={() => {
        setIsActive(client.is_active);
        setNotifications(client.notifications_enabled);
        setError(null);
      }}
    >
      <ToggleRow
        label="Portal active"
        sub={`When off, nobody at ${client.name} can sign in. Data is kept.`}
        checked={isActive}
        onChange={setIsActive}
      />
      <ToggleRow
        label="Email notifications"
        sub="Ticket updates, approval requests and status alerts go out by email."
        checked={notifications}
        onChange={setNotifications}
      />
    </Section>
  );
}

interface CompanyDraft {
  name: string;
  phone: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  webAddress: string;
}

function fromClient(c: AdminClientDetail): CompanyDraft {
  return {
    name: c.name,
    phone: c.phone ?? "",
    address1: c.address1 ?? "",
    address2: c.address2 ?? "",
    city: c.city ?? "",
    state: c.state ?? "",
    postalCode: c.postal_code ?? "",
    webAddress: c.web_address ?? "",
  };
}
